import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { Observable, Subject } from 'rxjs';

import { Catalog } from '@mtna/pojo-consumer-ui';

import { RdsApiService } from '../core/services/rds-api.service';
import {
  RdsDataProductSelectionDialogComponent,
  RdsDataProductSelectionDialogData,
  RdsDataProductSelectionDialogOutput,
} from './data-product-selection-dialog.component';

/**
 * Central service for managing the data product selection dialog.
 *
 * @author Will Davis <will.davis@mtna.us>
 */
@Injectable({ providedIn: 'root' })
export class RdsDataProductDialogService {
  private selectDialogRef: MatDialogRef<RdsDataProductSelectionDialogComponent>;

  constructor(private dialog: MatDialog, private rdsApiService: RdsApiService) {}

  /**
   * Open a select data source dialog if one is not already opened.
   *
   * @param rootCatalog Root RDS catalog
   * @param forceSelection Requires a selection to be made before closing
   * @param dialogWidth dialog width, defaults to 80vw
   * @returns Observable that emits whether a different data product was selected.
   */
  openSelectDataSourceDialog(rootCatalog: Catalog, forceSelection: boolean = false, dialogWidth: string = '80vw'): Observable<boolean> {
    const sub = new Subject<boolean>();
    if (!this.selectDialogRef) {
      // If the dialog is not already open

      // If a catalog is set, get the ID
      const currentCatalogId = this.rdsApiService.isCatalogIdSet() ? this.rdsApiService.getCatalogId() : null;
      // If a data product is set, get the ID
      const currentDataProductId = this.rdsApiService.isDataProductIdSet() ? this.rdsApiService.getDataProductId() : null;

      // Open the dialog
      this.selectDialogRef = this.dialog.open(RdsDataProductSelectionDialogComponent, {
        disableClose: forceSelection,
        width: dialogWidth,
        data: new RdsDataProductSelectionDialogData(
          rootCatalog,
          'Data Product Selection',
          'First choose a catalog, then pick a data product to work with.',
          currentCatalogId,
          currentDataProductId,
          forceSelection
        )
      });

      // Handle when the dialog closes
      this.selectDialogRef.afterClosed().subscribe((output: RdsDataProductSelectionDialogOutput) => {
        if (!!output) {
          // If there is an output, a selection was made,
          // check if the diloag selection is different than the current selection
          const changed: boolean = output.catalogId !== currentCatalogId || output.dataProductId !== currentDataProductId;
          if (changed) {
            // If the user selected something different, set those values
            this.rdsApiService.setCatalogId(output.catalogId);
            this.rdsApiService.setDataProductId(output.dataProductId);
          }
          sub.next(changed);
        } else {
          sub.next(false);
        }
        sub.complete();
        this.selectDialogRef = null;
      });
    } else {
      // The dialog is already opened, do nothing.
      sub.next(false);
      sub.complete();
    }
    return sub.asObservable();
  }
}
